/**
 * The browser's realtime connection (spec M0-F5 §5.4, §5.6, §5.7, §6).
 *
 * ## `transports: ['websocket']` is not a preference
 *
 * The api runs as a **pm2 cluster of 2** behind one nginx upstream, and HTTP long-polling
 * needs every request of a handshake to reach the same worker. Node's cluster round-robin
 * does not, which produces intermittent `Session ID unknown` in production and nowhere else.
 * So the polling transport is off at both ends, and a network that blocks websockets gets
 * the degraded path — `realtime.sync` on focus and every 30 s — rather than a broken one
 * (§5.4, X6, OQ-F5-1). `e2e` and a unit test both assert the option, because "it works on my
 * machine" is exactly the failure mode.
 *
 * ## Lazily connected
 *
 * The socket opens on the first `joinBoard`, never on module import: a browser that has just
 * posted the login form has no session cookie yet, and a socket opened before it would be
 * refused by the handshake and then reconnect-loop (E13, R1).
 */
import {
  PRESENCE_HEARTBEAT_MS,
  SOCKET_EVENTS,
  rooms,
  type EventEnvelope,
  type PresenceRoster,
} from '@elizmonday/shared';
import { io, type Socket } from 'socket.io-client';

export const SOCKET_PATH = '/socket.io';

/** What the status pill shows (§6, §8). */
export type RealtimeStatus = 'live' | 'connecting' | 'degraded' | 'offline';

let socket: Socket | null = null;
let status: RealtimeStatus = 'offline';
const statusListeners = new Set<(next: RealtimeStatus) => void>();
/** Consecutive failed connection attempts; the client calls itself degraded after a few. */
let failures = 0;
const DEGRADED_AFTER = 3;

function setStatus(next: RealtimeStatus): void {
  if (status === next) return;
  status = next;
  for (const listener of [...statusListeners]) listener(next);
}

export function realtimeStatus(): RealtimeStatus {
  return status;
}

export function onRealtimeStatus(listener: (next: RealtimeStatus) => void): () => void {
  statusListeners.add(listener);
  listener(status);
  return () => statusListeners.delete(listener);
}

/** Lazily created, so importing this module never opens a connection. */
export function getSocket(): Socket {
  if (socket) return socket;
  setStatus('connecting');
  socket = io({
    path: SOCKET_PATH,
    autoConnect: true,
    // See the module header. Do not add 'polling' here.
    transports: ['websocket'],
    upgrade: false,
    withCredentials: true,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
  });
  socket.on('connect', () => {
    failures = 0;
    setStatus('live');
  });
  socket.on('disconnect', () => {
    setStatus('connecting');
  });
  socket.on('connect_error', () => {
    /*
     * Swallowed on purpose — `connect_error` throws if nothing listens, and an unreachable
     * api is an expected state. After a few tries the client stops calling itself
     * "connecting" and admits it is degraded, which is what turns on the poll fallback.
     */
    failures += 1;
    if (failures >= DEGRADED_AFTER) setStatus('degraded');
  });
  return socket;
}

export interface JoinResult {
  room: string;
  serverTime: string | null;
  /** The inline reconnect delta, or `{syncRequired:true}` when it was too big (§5.6). */
  sync?: unknown;
}

/**
 * Join a board room, optionally with a resync watermark.
 *
 * Three answers, and they mean different things: a `JoinResult` is live; `'forbidden'` is
 * "this board is not yours any more" and the caller shows the no-access panel; `null` is
 * "no answer" — the api is down or the socket is blocked — which is *not* an error, it is
 * the degraded path.
 */
/**
 * How many mounted hooks hold each board room. Round 10k: the board screen mounts
 * `BoardViewRealtime` for **every** view kind, and a board's widget canvas now mounts
 * `useDashboardRealtime` beside it, so two owners of one room is no longer the impossible case
 * `BoardViewRealtime`'s comment describes — an unbalanced `leaveBoard` from either would take
 * the other's liveness with it. The counter makes join/leave idempotent per owner; the socket
 * message is still sent once, on the first join and the last leave.
 */
const boardRoomHolders = new Map<string, number>();

export function joinBoard(
  boardId: string,
  since?: string | null,
): Promise<JoinResult | 'forbidden' | null> {
  const expected = rooms.board(boardId);
  boardRoomHolders.set(boardId, (boardRoomHolders.get(boardId) ?? 0) + 1);
  return new Promise((resolve) => {
    let settled = false;
    const done = (value: JoinResult | 'forbidden' | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = setTimeout(() => done(null), 5_000);
    try {
      getSocket().emit(
        SOCKET_EVENTS.joinBoard,
        { boardId, ...(since ? { since } : {}) },
        (result: unknown) => {
          clearTimeout(timer);
          const answer = result as { ok?: boolean; code?: string; serverTime?: string; sync?: unknown };
          if (answer?.ok) {
            done({ room: expected, serverTime: answer.serverTime ?? null, ...(answer.sync ? { sync: answer.sync } : {}) });
            return;
          }
          done(answer?.code === 'FORBIDDEN' ? 'forbidden' : null);
        },
      );
    } catch {
      clearTimeout(timer);
      done(null);
    }
  });
}

export function leaveBoard(boardId: string): void {
  const holders = (boardRoomHolders.get(boardId) ?? 1) - 1;
  if (holders > 0) {
    boardRoomHolders.set(boardId, holders);
    return;
  }
  boardRoomHolders.delete(boardId);
  try {
    getSocket().emit(SOCKET_EVENTS.leaveBoard, { boardId });
  } catch {
    // Not connected; the server drops the room with the socket anyway.
  }
}

/**
 * **M8-P2 D1 (P-240)** — the account's branding and capability matrix changed somewhere else.
 *
 * It is the one subscription that opens the socket on its own: a person reading a board has one
 * already, a person sitting on `/` does not, and D1 is explicit that *"a second browser tab"*
 * repaints without a reload. The frame carries no data — the browser re-reads
 * `shell.bootstrap` with its own rights, and a socket is not an authorisation.
 */
export function onAccountChanged(handler: () => void): () => void {
  const active = getSocket();
  const listener = () => handler();
  active.on(SOCKET_EVENTS.accountChanged, listener);
  return () => {
    active.off(SOCKET_EVENTS.accountChanged, listener);
  };
}

export function onEvent(handler: (event: EventEnvelope) => void): () => void {
  const active = getSocket();
  const listener = (event: EventEnvelope) => handler(event);
  active.on(SOCKET_EVENTS.event, listener);
  return () => {
    active.off(SOCKET_EVENTS.event, listener);
  };
}

/** One subscription helper for the three server-to-client notices (§5.5, §5.8). */
export function onServerNotice(handlers: {
  presence?(roster: PresenceRoster): void;
  roomRevoked?(payload: { room: string; boardId: string }): void;
  resyncRequired?(payload: { reason: string }): void;
  connected?(): void;
}): () => void {
  const active = getSocket();
  // Socket.IO's `on`/`off` are typed `(...args: any[]) => void`; a narrower handler is safe
  // here because each is bound to the one event whose payload it declares.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type AnyListener = (...args: any[]) => void;
  const bindings: [string, AnyListener][] = [];
  if (handlers.presence) bindings.push([SOCKET_EVENTS.presence, handlers.presence as AnyListener]);
  if (handlers.roomRevoked) {
    bindings.push([SOCKET_EVENTS.roomRevoked, handlers.roomRevoked as AnyListener]);
  }
  if (handlers.resyncRequired) {
    bindings.push([SOCKET_EVENTS.resyncRequired, handlers.resyncRequired as AnyListener]);
  }
  if (handlers.connected) bindings.push(['connect', handlers.connected as AnyListener]);
  for (const [event, listener] of bindings) active.on(event, listener);
  return () => {
    for (const [event, listener] of bindings) active.off(event, listener);
  };
}

/* ── presence ─────────────────────────────────────────────────────────────── */

const heartbeats = new Map<string, ReturnType<typeof setInterval>>();

/**
 * Announce this client on a board and keep the entry alive. Stopping is what a returned
 * unsubscribe does; the TTL is what covers a browser that never gets the chance to
 * (§5.7 — a crashed tab disappears in 45 s with no cleanup path to maintain).
 */
export function enterPresence(boardId: string, viewId?: string | null): () => void {
  const active = getSocket();
  active.emit(SOCKET_EVENTS.presenceEnter, { boardId, ...(viewId ? { viewId } : {}) });
  const existing = heartbeats.get(boardId);
  if (existing) clearInterval(existing);
  heartbeats.set(
    boardId,
    setInterval(() => {
      active.emit(SOCKET_EVENTS.presenceHeartbeat, { boardId });
    }, PRESENCE_HEARTBEAT_MS),
  );
  return () => {
    const timer = heartbeats.get(boardId);
    if (timer) clearInterval(timer);
    heartbeats.delete(boardId);
    try {
      active.emit(SOCKET_EVENTS.presenceLeave, { boardId });
    } catch {
      // Disconnected; the TTL handles it.
    }
  };
}

/** `null` clears the marker. Advisory: the server never refuses a write because of it. */
export function markEditingCell(
  boardId: string,
  cell: { itemId: string; columnId: string } | null,
): void {
  try {
    getSocket().emit(SOCKET_EVENTS.presenceCell, { boardId, ...(cell ?? { itemId: null }) });
  } catch {
    // Not connected: presence is best-effort by design.
  }
}

/** Closes and forgets the singleton. Used by tests and by sign-out. */
export function disconnectSocket(): void {
  for (const timer of heartbeats.values()) clearInterval(timer);
  heartbeats.clear();
  if (!socket) return;
  try {
    socket.close();
  } catch {
    // Already closed.
  }
  socket = null;
  failures = 0;
  setStatus('offline');
}
